import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { DatatablesHelper, FormatHelper } from 'src/app/helpers';
import { Survey, SurveyTextareaAnswer } from 'src/app/models/survey';
import { AuthService } from 'src/app/services/auth.service';
import { CoursesService } from 'src/app/services/courses.service';
import { UIEvaluation, UIEvaluationScores } from './rentwise-evaluations.viewmodel';
import { SurveyQuestion, SurveyRadioTableAnswer } from 'src/app/models/survey';
import { UserSurvey } from 'src/app/models';

type Opt<T> = T|undefined;
type RawData = {
  columns: string[],
  data:    string[][]
};

@Component({
  selector: 'app-rentwise-evaluations',
  templateUrl: './rentwise-evaluations.component.html',
  styleUrls: ['./rentwise-evaluations.component.css']
})
export class RentwiseEvaluationsComponent implements OnInit, OnDestroy {
  preTest?: Survey;
  postTest?: Survey;
  evaluations: UIEvaluation[] = [];
  scores: (UIEvaluationScores|undefined)[][] = []; // per evaluation per question
  loaded = false;

  rawData = {  // dump for csv
    q: [] as RawData[] // for each question on survey
  };
  rawDataCols = [16, 24]; // to hide raw data by default
  rawDataEmpty: RawData = { columns: [], data: [] };

  agencyID?: string;
  agency?: any;

  userID?: string;
  progressID?: string;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of evaluations can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private coursesService: CoursesService
  ) { }

  ngOnInit(): void {
    this.initAgency();
    this.initTable();
    this.loadRentwiseEvaluations();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  public ui = {
    q: (v: Opt<UserSurvey>, qindex: number) => v?.data?.length ? v.data[qindex] : null,
    qtable: (v: Opt<UserSurvey>, qindex: number) => {
      const q = this.ui.q(v, qindex);
      return q?.answer?.type === 'radio_table' ? q as SurveyQuestion<SurveyRadioTableAnswer> : null;
    },
    qtext: (v: Opt<UserSurvey>, qindex: number) => {
      const q = this.ui.q(v, qindex);
      return q?.answer?.type === 'textarea' ? q as SurveyQuestion<SurveyTextareaAnswer> : null;
    },
    rawData: (index: number) => this.rawData.q[index] || this.rawDataEmpty
  };

  private initAgency() {
    this.agency = this.auth.agencyInfo;
    this.agencyID = this.auth.agencyInfo._id;
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip'
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'colvis',
        // 'excel'
        {
          extend: "excel",
          customize: function (xlsx: any) {
            let sheet = xlsx.xl.worksheets['sheet1.xml'];

            $('row* ', sheet).each(function(index) {
              if (index > 0) {
                  $(this).attr('ht', 30);
                  $(this).attr('customHeight', 1);
              }
            });
          },
          exportOptions: {
            stripNewlines: false,
            format: {
              body: function (data: string, row: number, column: number, node: any) {
                if ($(node).is('.td-answer.many-questions')) {
                  return DatatablesHelper.stripHtml(data);
                }
                else {
                  return data;
                }
              }
            }
          },
        },
      ]
    });
  }

  letter(index: number) {
    return FormatHelper.letter(index);
  }

  private async loadRentwiseEvaluations() {
    const query = {
      courseID:   'RentWise',
      agencyID:   this.agencyID || '',
      getusers:   true,
      getsurveys: true,
    };

    const res: any = await this.coursesService.getRentwiseEvaluations(query).toPromise();

    this.evaluations = res.evaluations || [];
    this.preTest = res.preTest;
    this.postTest = res.postTest;
    this.scores = this.evaluations.map(e => e.postTest ? e.postTest.data.map(q => q.answer.scores as UIEvaluationScores) : []);

    this.loadRawData();
    this.hideRawDataColumns();
    setTimeout(() => this.loadTable());
  }

  private loadRawData() {
    if (this.preTest && this.postTest) {
      const sharedQuestions = Math.min(this.preTest.questions.length, this.postTest.questions.length);

      this.rawData.q = _.range(sharedQuestions).map(i => {
        return this.rawDataTable(this.evaluations, i);
      });
    }
  }

  private rawDataTable(evaluations: UIEvaluation[], qindex: number) {
    const q = this.postTest?.questions[qindex];

    if (!q) {
      return this.rawDataEmpty;
    }

    const qtable = q.answer.type === 'radio_table' ? q.answer : null;

    if (qtable) {
      const columns = _.flatten(qtable.questions.map((q, i) => {
        return [
          `PreEval Q${qindex+1}${this.letter(i)}:\n${q}`,
          `PostEval Q${qindex+1}${this.letter(i)}:\n${q}`,
        ];
      }));

      const data = evaluations.map(e => {
        const pretable = this.ui.qtable(e.preTest, qindex);
        const posttable = this.ui.qtable(e.postTest, qindex);

        const preanswers = pretable?.answer.answers || [];
        const postanswers = posttable?.answer.answers || [];

        return _.flatten(qtable.questions.map((q, i) => [
          preanswers[i] || '',
          postanswers[i] || '',
        ]));
      });

      return { columns, data };
    }

    return this.rawDataEmpty;
  }

  private hideRawDataColumns() {
    let added = 0;

    const hideTargets = _.compact(this.rawData.q.map((q, index) => {
      if (!q.columns.length) {
        return null;
      }

      const count = q.columns.length;
      const pos   = this.rawDataCols[index]!;
      const start = added + pos;
      const cols  = _.range(start, start + count);

      added += cols.length - 1; // 1 reserved for container

      return cols;
    }));

    this.dtOptions.columnDefs = hideTargets.map(targets => ({
        // Indexes of columns to be hidden by default
        "targets": targets,
        "visible": false
    }));
  }

  private loadTable() {
    if (!this.loaded) {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
